import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../../../../utils/apiClient';

type RefundStatus = 'pending' | 'approved' | 'rejected' | 'processed' | string;

export interface RefundItem {
  id: string;
  refundId: string;
  bookingId: string | null;
  bookingServiceId: string | null;
  bookingAdditionalServiceId: string | null;
  bookingAdjustmentId: string | null;
  paymentId: string | null;
  refundScope: string;
  refundType: string;
  status: RefundStatus;
  refundMethod: string;
  gatewayRefundId: string;
  reason: string;
  notes: string;
  baseAmount: number;
  discountAmount: number;
  taxableAmount: number;
  cgstRate: number;
  sgstRate: number;
  cgstAmount: number;
  sgstAmount: number;
  totalRefundAmount: number;
  requestedByType: string;
  requestedById: string | null;
  processedByType: string;
  processedById: string | null;
  processedAt: string | null;
  createdAt: string;
  updatedAt: string;
  bookingCode: string;
  userId: string | null;
  customerName: string;
  customerMobile: string;
  bookingServiceName: string;
  additionalServiceName: string;
  serviceName: string;
  displayServiceName: string;
}

interface RefundsTabProps {
  bookingId?: number | string;
  refundsData?: unknown;
  bookingCodeFallback?: string;
}

type ApiRefund = {
  id?: number | string;
  refund_id?: number | string;
  booking_id?: number | string;
  bookingId?: number | string;
  booking_service_id?: number | string | null;
  bookingServiceId?: number | string | null;
  booking_additional_service_id?: number | string | null;
  bookingAdditionalServiceId?: number | string | null;
  booking_adjustment_id?: number | string | null;
  bookingAdjustmentId?: number | string | null;
  payment_id?: number | string | null;
  paymentId?: number | string | null;
  refund_scope?: string;
  refundScope?: string;
  refund_type?: string;
  refundType?: string;
  booking?: { id?: number | string; booking_code?: string };
  booking_code?: string;
  bookingCode?: string;
  service_id?: number | string | null;
  serviceId?: number | string | null;
  service_name?: string;
  booking_service_name?: string | null;
  bookingServiceName?: string | null;
  additional_service_name?: string | null;
  additionalServiceName?: string | null;
  related_to?: string;
  relatedTo?: string;
  amount?: number | string;
  refund_amount?: number | string;
  totalRefundAmount?: number | string;
  base_amount?: number | string;
  baseAmount?: number | string;
  discount_amount?: number | string;
  discountAmount?: number | string;
  taxable_amount?: number | string;
  taxableAmount?: number | string;
  cgst_rate?: number | string;
  cgstRate?: number | string;
  sgst_rate?: number | string;
  sgstRate?: number | string;
  cgst_amount?: number | string;
  cgstAmount?: number | string;
  sgst_amount?: number | string;
  sgstAmount?: number | string;
  total_refund_amount?: number | string;
  requested_by_type?: string | null;
  requestedByType?: string | null;
  requested_by_id?: number | string | null;
  requestedById?: number | string | null;
  processed_by_type?: string | null;
  processedByType?: string | null;
  processed_by_id?: number | string | null;
  processedById?: number | string | null;
  processed_at?: string | null;
  processedAt?: string | null;
  status?: string;
  refund_method?: string | null;
  refundMethod?: string | null;
  gateway_refund_id?: string | null;
  gatewayRefundId?: string | null;
  notes?: string | null;
  note?: string | null;
  cancellation_note?: string | null;
  reason?: string;
  refund_reason?: string;
  refundReason?: string;
  user_id?: number | string | null;
  userId?: number | string | null;
  customer_name?: string | null;
  customerName?: string | null;
  customer_mobile?: string | null;
  customerMobile?: string | null;
  created_at?: string;
  createdAt?: string;
  updated_at?: string;
  updatedAt?: string;
};

const toNumber = (value: unknown, fallback = 0): number => {
  if (value === null || value === undefined || value === '') return fallback;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toTitleCase = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const toArray = (value: unknown): ApiRefund[] => {
  if (Array.isArray(value)) return value as ApiRefund[];
  return [];
};

const normalizeRefund = (item: ApiRefund, bookingCodeFallback?: string): RefundItem => {
  const resolvedId = String(item.refund_id ?? item.id ?? `refund-${Date.now()}`);
  const bookingId = item.booking_id ?? item.bookingId ?? item.booking?.id ?? null;
  const hasServiceReference =
    item.booking_service_id !== null && item.booking_service_id !== undefined
      ? true
      : item.bookingServiceId !== null && item.bookingServiceId !== undefined
      ? true
      : item.service_id !== null && item.service_id !== undefined
      ? true
      : item.serviceId !== null && item.serviceId !== undefined;
  const resolvedRefundScope =
    item.refund_scope ||
    item.refundScope ||
    (hasServiceReference ? 'service' : 'booking');
  const displayServiceName =
    item.service_name ||
    item.bookingServiceName ||
    item.booking_service_name ||
    item.additionalServiceName ||
    item.additional_service_name ||
    item.relatedTo ||
    item.related_to ||
    'Booking';

  return {
    id: resolvedId,
    refundId: resolvedId,
    bookingId: bookingId !== null ? String(bookingId) : null,
    bookingServiceId:
      item.booking_service_id !== null && item.booking_service_id !== undefined
        ? String(item.booking_service_id)
        : null,
    bookingAdditionalServiceId:
      item.booking_additional_service_id !== null && item.booking_additional_service_id !== undefined
        ? String(item.booking_additional_service_id)
        : null,
    bookingAdjustmentId:
      item.booking_adjustment_id !== null && item.booking_adjustment_id !== undefined
        ? String(item.booking_adjustment_id)
        : null,
    paymentId:
      item.payment_id !== null && item.payment_id !== undefined
        ? String(item.payment_id)
        : item.paymentId !== null && item.paymentId !== undefined
        ? String(item.paymentId)
        : null,
    refundScope: resolvedRefundScope,
    refundType: item.refund_type || item.refundType || '-',
    status: String(item.status || 'pending').toLowerCase(),
    refundMethod: item.refund_method || item.refundMethod || '-',
    gatewayRefundId: item.gateway_refund_id || item.gatewayRefundId || '-',
    reason: item.refund_reason || item.refundReason || item.reason || '-',
    notes: item.notes || item.note || item.cancellation_note || '',
    baseAmount: toNumber(item.base_amount ?? item.baseAmount),
    discountAmount: toNumber(item.discount_amount ?? item.discountAmount),
    taxableAmount: toNumber(item.taxable_amount ?? item.taxableAmount),
    cgstRate: toNumber(item.cgst_rate ?? item.cgstRate),
    sgstRate: toNumber(item.sgst_rate ?? item.sgstRate),
    cgstAmount: toNumber(item.cgst_amount ?? item.cgstAmount),
    sgstAmount: toNumber(item.sgst_amount ?? item.sgstAmount),
    totalRefundAmount: toNumber(
      item.total_refund_amount ?? item.totalRefundAmount ?? item.refund_amount ?? item.amount
    ),
    requestedByType: item.requested_by_type || item.requestedByType || '-',
    requestedById:
      item.requested_by_id !== null && item.requested_by_id !== undefined
        ? String(item.requested_by_id)
        : item.requestedById !== null && item.requestedById !== undefined
        ? String(item.requestedById)
        : null,
    processedByType: item.processed_by_type || item.processedByType || '-',
    processedById:
      item.processed_by_id !== null && item.processed_by_id !== undefined
        ? String(item.processed_by_id)
        : item.processedById !== null && item.processedById !== undefined
        ? String(item.processedById)
        : null,
    processedAt: item.processed_at || item.processedAt || null,
    createdAt: item.created_at || item.createdAt || new Date().toISOString(),
    updatedAt: item.updated_at || item.updatedAt || item.created_at || item.createdAt || new Date().toISOString(),
    bookingCode: item.booking_code || item.bookingCode || item.booking?.booking_code || bookingCodeFallback || '-',
    userId:
      item.user_id !== null && item.user_id !== undefined
        ? String(item.user_id)
        : item.userId !== null && item.userId !== undefined
        ? String(item.userId)
        : null,
    customerName: item.customer_name || item.customerName || '-',
    customerMobile: item.customer_mobile || item.customerMobile || '-',
    bookingServiceName: item.booking_service_name || item.bookingServiceName || '-',
    additionalServiceName: item.additional_service_name || item.additionalServiceName || '-',
    serviceName: item.service_name || '-',
    displayServiceName,
  };
};

const getStatusClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'approved':
    case 'processed':
      return 'bg-green-100 text-green-700';
    case 'rejected':
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-yellow-100 text-yellow-700';
  }
};

const RefundsTab: React.FC<RefundsTabProps> = ({ bookingId, refundsData, bookingCodeFallback }) => {
  const navigate = useNavigate();
  const [refunds, setRefunds] = useState<RefundItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setRefundRows = (raw: ApiRefund[]) => {
    const normalized = raw.map((item) => normalizeRefund(item, bookingCodeFallback));
    const hasBookingIds = normalized.some((item) => item.bookingId !== null);
    const filtered =
      bookingId && hasBookingIds
        ? normalized.filter((item) => item.bookingId === String(bookingId))
        : normalized;

    setRefunds(filtered);
  };

  const fetchRefunds = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await apiCall({
        endpoint: 'admin/booking/refunds',
        method: 'GET',
      });

      if (res && (res.status === 200 || res.success === true)) {
        const directData = toArray(res.data);
        const nestedData = toArray((res.data as { data?: unknown } | null)?.data);
        const nestedRefunds = toArray((res.data as { refunds?: unknown } | null)?.refunds);
        const raw = directData.length
          ? directData
          : nestedData.length
          ? nestedData
          : nestedRefunds;

        setRefundRows(raw);
        return;
      }

      setError(res?.message || 'Failed to load refunds');
    } catch (err) {
      console.error('Error fetching refunds:', err);
      setError('Failed to load refunds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (refundsData !== undefined) {
      setError(null);
      setLoading(false);
      setRefundRows(toArray(refundsData));
      return;
    }

    fetchRefunds();
  }, [bookingId, refundsData]);

  const totalRefund = useMemo(
    () => refunds.reduce((sum, item) => sum + Math.abs(item.totalRefundAmount), 0),
    [refunds]
  );

  return (
    <div className="mb-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-800">Refunds</h3>
      </div>

      <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Refund Amount</p>
            <p className="text-2xl font-bold text-blue-700">Rs {totalRefund.toFixed(2)}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Refund Requests</p>
            <p className="text-2xl font-bold">{refunds.length}</p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Refund ID</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Service</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Reason</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Method</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {refunds.length > 0 ? (
              refunds.map((item, index) => (
                <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-4 py-3 font-medium text-gray-800">{item.refundId}</td>
                  <td className="px-4 py-3 text-gray-700">{item.displayServiceName}</td>
                  <td className="px-4 py-3 text-gray-700">{item.reason}</td>
                  <td className="px-4 py-3">
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">
                      Rs {Math.abs(item.totalRefundAmount).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${getStatusClass(item.status)}`}>
                      {toTitleCase(item.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{toTitleCase(item.refundMethod || '-')}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => navigate('/refund-details', { state: { refund: item, bookingId } })}
                      className="rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="py-8 text-center text-gray-500">
                  {loading ? 'Loading refunds...' : 'No refund requests found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {error && <div className="mt-3 text-sm text-red-600">{error}</div>}
    </div>
  );
};

export default RefundsTab;
